import { useEffect, useState } from "react";
import { useErrorHandler } from "./useErrorHandler";
import { METER_SIDEBAR_SELECTOR, PAGE_TABS_ACCESSORS } from "../constants";
import { MeterReturnType, MetersReturnType, PropertyType } from "../types";
import { fetchDashboardPropertiesAsync } from "../actions/properties.actions";
import { fetchPropertyMetersAsync } from "../actions/meters.actions";
import { useMeterStore } from "../store/meter.store";

export default function useDashboardHook() {
  const { handleError } = useErrorHandler();
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [selectedPageTab, setSelectedPageTab] = useState(
    PAGE_TABS_ACCESSORS.meter.value
  );
  const {
    selectedMeter,
    setSelectedMeter,
    meterSidebarSelector,
    setMeterSidebarSelector,
    selectedMetersForSharing,
    toggleMeterForSharing,
    clearSelectedMetersForSharing,
  } = useMeterStore((state) => state);

  const closeMeterOverviewSidebar = () => {
    setSelectedMeter(null);
    setMeterSidebarSelector(null);
    clearSelectedMetersForSharing();
  };

  const onShareClick = () => {
    setMeterSidebarSelector(METER_SIDEBAR_SELECTOR.meterShare);
  };

  const useProperties = () => {
    const [properties, setProperties] = useState<PropertyType[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchProperties = async () => {
      setLoading(true);

      fetchDashboardPropertiesAsync()
        .then((res: any) => {
          if (res?.data?.properties?.length) {
            setSelectedPropertyId(res?.data?.properties?.[0]?.propertyId);
          }
          setProperties(res?.data?.properties);
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);

          handleError(error);
        });
    };

    const handlePropertySelect = (propertyId: string) => {
      setSelectedPropertyId(propertyId);

      setSelectedMeter(null);
    };

    useEffect(() => {
      fetchProperties();
    }, []);

    return {
      properties,
      loading,
      selectedPropertyId,
      handlePropertySelect,
    };
  };

  const usePageTabs = () => {
    const handlePageTabSelect = (tab: string) => {
      setSelectedPageTab(tab);
    };

    return {
      handlePageTabSelect,
    };
  };

  const useMeters = (propertyId: string) => {
    const [meters, setMeters] = useState<MetersReturnType>([]);
    const [loading, setLoading] = useState(false);

    const fetchMeters = async () => {
      setLoading(true);

      fetchPropertyMetersAsync(propertyId)
        .then((res: any) => {
          setMeters(res?.data);
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);

          handleError(error);
        });
    };

    const handleMeterSelect = (meter: MeterReturnType | null) => {
      setSelectedMeter(meter);

      setMeterSidebarSelector(METER_SIDEBAR_SELECTOR.meterOverview);
    };

    const onMenuClick = (meter: MeterReturnType) => {
      setSelectedMeter(meter);

      setMeterSidebarSelector(METER_SIDEBAR_SELECTOR.meterDetails);
    };

    const onAllReadingsClick = (meter: MeterReturnType) => {
      setSelectedMeter(meter);

      setMeterSidebarSelector(METER_SIDEBAR_SELECTOR.meterPayloadDetails);
    };

    useEffect(() => {
      if (propertyId) {
        fetchMeters();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertyId]);

    return {
      meters,
      loading,
      handleMeterSelect,
      onMenuClick,
      onAllReadingsClick,
      selectedMetersForSharing,
      toggleMeterForSharing,
      onShareClick,
    };
  };

  useEffect(() => {
    setSelectedMeter(null);
    setMeterSidebarSelector(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    useProperties,
    usePageTabs,
    useMeters,
    selectedPropertyId,
    selectedMeter,
    selectedPageTab,
    closeMeterOverviewSidebar,
    meterSidebarSelector,
    setMeterSidebarSelector,
    selectedMetersForSharing,
  };
}
